import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';

function normalizeCompanyCode(value) {
  return String(value || 'PINE').trim().toUpperCase() || 'PINE';
}

// kind = 'freelancer' 면 이 회사 테넌트의 3.3% 부서 목록(2026-11-03 합치기-2) — 4대보험 회사 부서와 따로 추가·이름 바꾸기·삭제.
//   서버가 회사 → 3.3% 묶음(FL_<테넌트>)으로 바꿔 준다. 이름 바꾸기·삭제는 부서 번호로 가니 그대로.
export function useDepartmentSettings({
  companyCode = 'PINE',
  deptType = 'internal',
  kind = '',
  checkAccess = false,
  showToast,
  onChanged,
}) {
  const normalizedCompanyCode = normalizeCompanyCode(companyCode);
  const kindQuery = kind === 'freelancer' ? '&kind=freelancer' : '';
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // checkAccess: 이 회사 부서를 고칠 수 있나 — 관리 목록 GET 이 200 일 때만 true(fail-closed).
  //   직원 관리 ☰ 부서 설정과 등록·수정 창 '+ 새 부서 추가'(2026-11-03)가 같이 쓴다.
  const [canManage, setCanManage] = useState(false);
  useEffect(() => {
    if (!checkAccess) return undefined;
    let alive = true;
    setCanManage(false);
    apiClient.get(`/api/admin/departments?company_code=${encodeURIComponent(normalizedCompanyCode)}${kindQuery}`)
      .then((res) => { if (alive) setCanManage(Boolean(res.ok && res.data?.success)); })
      .catch(() => { if (alive) setCanManage(false); });
    return () => { alive = false; };
  }, [checkAccess, normalizedCompanyCode, kindQuery]);

  const fetchDepartments = useCallback(async () => {
    setLoading(true);
    const result = await apiClient.get(
      `/api/departments?company_code=${encodeURIComponent(normalizedCompanyCode)}${kindQuery}`,
    );
    setLoading(false);

    if (!result.ok || !result.data?.success) {
      showToast?.(result.data?.message || '부서 목록을 불러오지 못했습니다.', 'error');
      return;
    }
    setDepartments(Array.isArray(result.data.departments) ? result.data.departments : []);
  }, [normalizedCompanyCode, kindQuery, showToast]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const departmentNames = useMemo(
    () => departments.map((dept) => dept.name).filter(Boolean),
    [departments],
  );

  async function reloadAfterChange(message) {
    showToast?.(message, 'success');
    await fetchDepartments();
    onChanged?.();
  }

  async function addDepartment(name) {
    const trimName = String(name || '').trim();
    if (!trimName) {
      showToast?.('부서명을 입력해 주세요.', 'error');
      return false;
    }

    setSaving(true);
    const result = await apiClient.post('/api/admin/departments', {
      company_code: normalizedCompanyCode,
      type: deptType,
      name: trimName,
      ...(kindQuery ? { kind: 'freelancer' } : {}),
    });
    setSaving(false);

    if (!result.ok || !result.data?.success) {
      showToast?.(result.data?.message || '부서 추가에 실패했습니다.', 'error');
      return false;
    }
    await reloadAfterChange('부서를 추가했습니다.');
    return true;
  }

  async function renameDepartment(id, name) {
    const trimName = String(name || '').trim();
    if (!trimName) {
      showToast?.('부서명을 입력해 주세요.', 'error');
      return false;
    }

    setSaving(true);
    const result = await apiClient.put(`/api/admin/departments/${id}`, {
      name: trimName,
    });
    setSaving(false);

    if (!result.ok || !result.data?.success) {
      showToast?.(result.data?.message || '부서 수정에 실패했습니다.', 'error');
      return false;
    }
    await reloadAfterChange('부서를 수정했습니다.');
    return true;
  }

  async function deleteDepartment(id, force = false) {
    setSaving(true);
    const result = await apiClient.del(
      `/api/admin/departments/${id}?force=${force ? 'true' : 'false'}`,
    );
    setSaving(false);

    if (!result.ok) {
      showToast?.(result.data?.message || '부서 삭제에 실패했습니다.', 'error');
      return false;
    }
    if (result.data?.hasUsers && !force) {
      const ok = window.confirm(
        `이 부서에 직원 ${result.data.count || 0}명이 있습니다. 그래도 삭제하시겠습니까?`,
      );
      if (!ok) return false;
      return deleteDepartment(id, true);
    }
    if (!result.data?.success) {
      showToast?.(result.data?.message || '부서 삭제에 실패했습니다.', 'error');
      return false;
    }
    await reloadAfterChange('부서를 삭제했습니다.');
    return true;
  }

  return {
    departments,
    departmentNames,
    canManage,
    loading,
    saving,
    fetchDepartments,
    addDepartment,
    renameDepartment,
    deleteDepartment,
  };
}
